import { Tag } from "antd";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";

import { CustomDropdown, PaginationComponent } from "components";
import ArticlesList from "./ArticlesList";
import MobileNav from "./MobileNav";
import ProductsFilter from "./ProductsFilter";

import { BASE_URL } from "config/constants";
import {
  fetchFilteredArticles,
  fetchFiltersList,
} from "store/articles/articles.slice";
import { setSEO } from "utils/helpers/seo-function";
import { truncateString } from "utils/helpers/strings";
import { CURRENCY } from "utils/textConstants";

import { ArticleSortOrder, sortOrderItems } from "types/articles/SortArticle";
import { parseNumberParam } from "utils/helpers/parseParam";
import "./ArticlesPage.scss";

interface ProductsPageProps {
  cat1?: string;
  cat2?: string;
  cat3?: string;
  menu?: any;
}

const ArticlesPage: React.FC<ProductsPageProps> = ({
  cat1,
  cat2,
  cat3,
  menu,
}) => {
  const dispatch: any = useDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const slug = useMemo(() => cat3 ?? cat2 ?? cat1 ?? null, [cat1, cat2, cat3]);

  // Derived URL params
  const currentPage = useMemo(
    () => parseNumberParam(searchParams, "page", 1),
    [searchParams]
  );
  const pageSize = useMemo(
    () => parseNumberParam(searchParams, "take", 12),
    [searchParams]
  );

  // Iz state-a uzmi definicije filtera
  const { data: filtersData, is_fetch: filtersLoaded } = useSelector(
    (s: any) => s.articles.filters_list
  );
  // Pronađi price-filter (filter_type === "scroll")
  const { min: defaultMinPrice, max: defaultMaxPrice } = useMemo(() => {
    const priceFilter = filtersData.find(
      (f: any) => f.filter_type === "scroll"
    );
    if (!priceFilter) return { min: 0, max: 0 };
    const [minChild, maxChild] = priceFilter.filter_children;
    return {
      min: minChild.value_number,
      max: maxChild.value_number,
    };
  }, [filtersData]);

  const minPrice = useMemo(
    () => parseNumberParam(searchParams, "min_price", defaultMinPrice),
    [searchParams, defaultMinPrice]
  );
  const maxPrice = useMemo(
    () => parseNumberParam(searchParams, "max_price", defaultMaxPrice),
    [searchParams, defaultMaxPrice]
  );
  const sortOrder = useMemo(
    () => searchParams.get("sort") ?? ArticleSortOrder.Default,
    [searchParams]
  );

  // grupisi sve ostale parametre izuzev sort, page, take, min_price i max_price u activeFilters
  // to su filteri svi koje treba prikazati
  const activeFilters = useMemo(() => {
    const grouped: Record<string, string[]> = {};
    searchParams.forEach((value, key) => {
      if (!["sort", "page", "take", "min_price", "max_price"].includes(key)) {
        (grouped[key] ??= []).push(value);
      }
    });
    return Object.entries(grouped).map(([filter_label, filter_values]) => ({
      filter_label,
      filter_values,
    }));
  }, [searchParams]);

  // uzmi sve URL parametre osim page, take, sort
  //ovo nam treba za pozivanje API-ja
  const getFiltersFromURL = (): {
    filter_label: string;
    filter_values: string;
  }[] =>
    Array.from(new URLSearchParams(location.search).entries())
      .filter(
        ([k]) => !["sort", "page", "take"].includes(k)
        //pitanje da li ovde iskljuciti price
      )
      .map(([filter_label, filter_values]) => ({
        filter_label,
        filter_values,
      }));
  // fetch available filter definitions
  useEffect(() => {
    if (slug)
      dispatch(
        fetchFiltersList({ ac_slug: slug, url_filters: getFiltersFromURL() })
      );
  }, [dispatch, slug, location.pathname]);

  // fetch articles when any param changes
  useEffect(() => {
    if (!slug) return;
    const data: any = { ac_slug: slug, page: currentPage, take: pageSize };
    if (sortOrder !== ArticleSortOrder.Default) data.order = sortOrder;
    data.min_price = minPrice;
    data.max_price = maxPrice;
    if (activeFilters.length)
      data.article_filters = JSON.stringify({ article_filters: activeFilters });
    dispatch(fetchFilteredArticles(data));
  }, [
    dispatch,
    slug,
    currentPage,
    pageSize,
    sortOrder,
    minPrice,
    maxPrice,
    activeFilters,
  ]);

  // --- Handleri koji resetuju page na 1 ---
  // Helper za promenu veličine stranice i reset page
  const handlePageSizeChange = (pageSize: string) => {
    const p = new URLSearchParams(searchParams);
    p.set("take", String(pageSize));
    p.set("page", "1"); // resetujemo na prvu stranicu
    setSearchParams(p, { replace: true });
    window.scrollTo(0, 0);
  };
  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", String(newPage));
    setSearchParams(params, { replace: true });
    window.scrollTo(0, 0);
  };

  const handleSortSelect = (key: string) => {
    const p = new URLSearchParams(searchParams);
    if (key === ArticleSortOrder.Default) {
      p.delete("sort");
    } else {
      p.set("sort", key);
    }
    p.set("page", "1");
    setSearchParams(p, { replace: true });
    window.scrollTo(0, 0);
  };

  const handleFilterToggle = (filterKey: string, values: string) => {
    const p = new URLSearchParams(searchParams);
    const existing = p.getAll(filterKey);
    p.delete(filterKey);
    const next = existing.includes(values)
      ? existing.filter((v) => v !== values)
      : [...existing, values];
    next.forEach((v) => p.append(filterKey, v));
    p.set("page", "1"); // <<< reset page
    setSearchParams(p, { replace: true });
    window.scrollTo(0, 0);
  };

  const handlePriceChange = (range: [number | null, number | null]) => {
    const [min, max] = range;
    const p = new URLSearchParams(searchParams);
    if (min != null) p.set("min_price", String(min));
    else p.delete("min_price");
    if (max != null) p.set("max_price", String(max));
    else p.delete("max_price");
    p.set("page", "1"); // <<< reset page
    setSearchParams(p, { replace: true });
    window.scrollTo(0, 0);
  };

  const handleCloseAppliedFilter = (filter: {
    filter_label: string;
    filter_values: string[];
  }) => {
    const params = new URLSearchParams(searchParams);
    params.delete(filter.filter_label); // briše sve vrednosti za datu labelu
    params.set("page", "1"); // resetuj paginaciju
    setSearchParams(params, { replace: true });
    window.scrollTo(0, 0);
  };

  const handleClosePrice = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("min_price");

    params.delete("max_price");
    params.set("page", "1");
    setSearchParams(params, { replace: true });
    window.scrollTo(0, 0);
  };

  // --- Render ---
  //Iz state-a uzmi fetchovane artikle koji su filtrirani
  const {
    list: products,
    meta: productsMeta,
    is_fetch: productsLoaded,
  } = useSelector((s: any) => s.articles.filtered_articles);

  const getValidOrderValue = (value: string): "Valid" | "Invalid" => {
    if (Object.values(ArticleSortOrder).includes(value as ArticleSortOrder)) {
      return "Valid";
    } else {
      return "Invalid";
    }
  };
  const { t } = useTranslation();
  const getTitle = () => {
    // let title = t("proizvodi") || "Proizvodi";
    // let items = [];
    // if (menu.is_fetch) {
    //   if (cat1) {
    //     items = menu.article_category.filter((x: any) => x.slug === cat1);
    //     title = items[0] ? items[0].ac_name : "";
    //   }
    //   if (cat2) {
    //     items = items[0]
    //       ? items[0].subcategories.filter((y: any) => y.slug === cat2)
    //       : items;
    //     title = items[0] ? items[0].ac_name : "";
    //   }
    //   if (cat3) {
    //     items = items[0]
    //       ? items[0].subcategories.filter((z: any) => z.slug === cat3)
    //       : items;
    //     title = items[0] ? items[0].ac_name : "";
    //   }
    // }
    // return title;
    // Defaultni naslov
    let title = t("proizvodi") || "Proizvodi";

    // Ako još nema menija učitanog, vratimo default
    if (!menu.is_fetch) return title;

    // Skup svih nivoa slug-ova koji postoje
    const slugs = [cat1, cat2, cat3].filter(Boolean) as string[];

    // Početni niz kategorija je top-level
    let candidates = menu.article_category as Array<any>;

    // Iteriramo kroz svaki slug i "ronimo" dublje u hijerarhiju
    for (const slug of slugs) {
      const found = candidates.find((x: any) => x.slug === slug);
      if (!found) break; // ako ga nema, prekidamo dalje traženje
      title = found.ac_name; // ažuriramo title
      candidates = found.subcategories || []; // spuštamo se u podkategorije
    }

    return title;
  };

  useEffect(() => {
    if (!productsLoaded || !filtersLoaded) return;

    const params = new URLSearchParams(location.search);
    const keywords: string[] = [];

    // sortiranje
    if (
      sortOrder !== ArticleSortOrder.Default &&
      getValidOrderValue(sortOrder) === "Valid"
    ) {
      keywords.push(`order: ${sortOrder}`);
    }
    // cena
    if (minPrice != null && minPrice >= defaultMinPrice) {
      keywords.push(`min_price: ${minPrice}`);
    }
    if (maxPrice != null && maxPrice <= defaultMaxPrice) {
      keywords.push(`max_price: ${maxPrice}`);
    }
    // ostali filteri
    activeFilters.forEach((f) => {
      keywords.push(`${f.filter_label}: ${f.filter_values.join(", ")}`);
    });

    setSEO({
      title: getTitle(),
      description: getTitle(),
      keywords,
      url: `${BASE_URL}${location.pathname}?${params.toString()}`,
      image: "",
      type: "articles",
    });
  }, [
    productsLoaded,
    filtersLoaded,
    sortOrder,
    minPrice,
    maxPrice,
    activeFilters,
    location.pathname,
    location.search,
    setSEO,
  ]);

  return (
    <>
      <div className="ProductPageTitle">
        <h2 className="Page-Title">{getTitle()}</h2>
        {productsLoaded && products.length === 0 && (
          <label>Nisu pronađeni proizvodi</label>
        )}
      </div>

      <div className="Products-Page-Row1">
        <div className="Products-Page-Row1-Column">
          {filtersLoaded && filtersData.length !== 0 && (
            <MobileNav
              slug={slug}
              minPrice={minPrice}
              maxPrice={maxPrice}
              filtersData={filtersData}
              handleFilterToggle={handleFilterToggle}
              handlePriceChange={handlePriceChange}
              searchParams={searchParams}
            />
          )}
        </div>
        {/* sort dropdown  */}
        {productsLoaded && products.length !== 0 && (
          <CustomDropdown
            items={sortOrderItems}
            selectedKey={sortOrder}
            onChange={handleSortSelect}
            placeholder={t("Sortiranje")}
          />
        )}
      </div>
      <div className="Products-Page-Row2">
        <div className="Products-Page-Row2-Col1">
          {filtersLoaded && filtersData.length !== 0 && (
            <ProductsFilter
              minPrice={minPrice}
              maxPrice={maxPrice}
              filtersData={filtersData}
              handleFilterToggle={handleFilterToggle}
              handlePriceChange={handlePriceChange}
              searchParams={searchParams}
            />
          )}
        </div>
        <div className="Products-Page-Row2-Col2">
          <div className="Applied-Filters">
            {/* {(activeFilters.length > 0 ||
              minPrice !== defaultMinPrice ||
              maxPrice !== defaultMaxPrice) && (
              <button
                className="Applied-Filter-Button"
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  Array.from(params.keys()).forEach((key) => {
                    if (!["page", "take", "sort"].includes(key)) {
                      params.delete(key);
                    }
                  });
                  params.delete("min_price");
                  params.delete("max_price");
                  params.set("page", "1");
                  setSearchParams(params, { replace: true });
                }}
              >
                Ukloni sve
              </button>
            )} */}

            {activeFilters.length > 0 &&
              activeFilters.map((filter: any) => {
                const text =
                  filter.filter_label + ": " + filter.filter_values.join(", ");

                return (
                  <Tag
                    className="Applied-Filter-Tag"
                    color="default"
                    bordered={false}
                    closable
                    onClose={() => handleCloseAppliedFilter(filter)}
                    key={filter.filter_label}
                  >
                    {truncateString(text, 50)}
                  </Tag>
                );
              })}

            {(minPrice !== defaultMinPrice || maxPrice !== defaultMaxPrice) && (
              <Tag
                className="Applied-Filter-Tag"
                color="default"
                bordered={false}
                closable
                // onClose={() => handlePriceChange([null, null])}
                onClose={handleClosePrice}
                key="price-filter"
              >
                Cijena:{" "}
                {minPrice === defaultMaxPrice ? defaultMaxPrice : minPrice} -{" "}
                {maxPrice === defaultMaxPrice ? defaultMaxPrice : maxPrice}{" "}
                {CURRENCY}
              </Tag>
            )}
          </div>

          <div className="Articles-List">
            <ArticlesList
              products={products}
              productsLoaded={productsLoaded}
              // productsMeta={productsMeta}
            />
            {productsLoaded && (
              <PaginationComponent
                current={currentPage}
                totalItems={productsMeta.item_count}
                pageSize={pageSize}
                hasNextPage={productsMeta.has_next_page}
                changePageSize={handlePageSizeChange}
                changePage={handlePageChange}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ArticlesPage;
